import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { message, Modal } from "antd";
import { useTranslation } from "react-i18next";
import { generateRandomString } from "utils/helper";
import UserForm from "../components/UserForm";
import * as services from "../services/userManagementApi";
import * as slices from "../slices/userSlice";
import { setIsLoading, setUserList } from "../slices/userSlice";

function toParams(params = {}) {
  const { pagination = {}, sortBy = [] } = params;
  const modifiedParams = {
    pagination: {
      page: pagination?.page,
      size: pagination?.pageSize,
    },
    sortBy: sortBy
      .filter((s) => s.order !== undefined)
      .map((s) => ({
        direction: s.order === "descend" ? "desc" : "asc",
        property: s.field,
      })),
  };
  return modifiedParams;
}

function useUserManagement() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const currentEmployee = useSelector((state) => state.company.currentEmployee);
  const isLoading = useSelector((state) => state.user.isLoading);
  const userList = useSelector((state) => state.user.userList);
  const roleOptions = useSelector((state) => state.user.roleOptions)
  const supervisorOptions = useSelector(
    (state) => state.user.supervisorOptions
  );

  const [pagination, setPagination] = useState({ current: 1, page: 1, size: 10 });
  const [openEditModal, setOpenEditModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentUser, setUser] = useState({});
  // const [roleOption, setRoleOption] = useState([]);
  // const [supervisorOption, setSupervisorOption] = useState([]);
  const [search, setSearch] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);

  const getUserList = useCallback(
    async (params = {}) => {
      try {
        dispatch(setIsLoading(true));
        const response = await services.getUserList({
          ...params.search,
          requestId: generateRandomString(),
          companyId: currentCompany,
          ...params.pagination,
          sortBy: params.sortBy,
        });
        dispatch(setUserList(response?.data?.content || []));
        setPagination({
          ...pagination,
          // ...params?.request,
          current: response?.data?.pageNo + 1,
          pageNo: response?.data?.pageNo + 1,
          total: response.data?.totalItems,
        });
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, pagination]
  );

  const onChange = useCallback(
    (tablePagination, tableFilters, tableSorter, tableExtra) => {
      const modifiedSorter = Array.isArray(tableSorter)
        ? [...tableSorter]
        : [tableSorter];

      // if (modifiedSorter.filter((s) => s.order !== undefined).length == 0) {
      //   modifiedSorter.push({
      //     order: 'asc',
      //     field: 'createDate',
      //   })
      // }
      const modifiedParams = toParams({
        pagination: {
          page: tablePagination.current - 1,
          pageSize: tablePagination.pageSize,
          current: tablePagination.current - 1,
        },
        sortBy: modifiedSorter,
      });
      setPagination({ ...pagination, ...modifiedParams?.pagination })
      getUserList({ ...modifiedParams, request: pagination.request, search });
    },
    [getUserList, pagination]
  );


  const getSupervisorUserList = useCallback(
    async (params = {}) => {
      try {
        dispatch(setIsLoading(true));
        const response = await services.getUserList({
          requestId: generateRandomString(),
          companyId: currentCompany,
          ...params.pagination,
          sortBy: params.sortBy,
        });
        // console.log("response", response);
        dispatch(setUserList(response?.data?.content || []));
        const supervisorData = response?.data?.content.map((item) => ({
          label: i18n.language === "en" ? (item?.nameEn + ' ' + item?.surNameEn) : (item?.name + ' ' + item?.surName),
          name: item.name,
          value: item.id
        }))
        // setSupervisorOption(supervisorData)
        setPagination({
          ...pagination,
          current: 1,
          pageNo: 1,
          total: response.data?.totalItems,
        });
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        // getRoleOptions();
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, pagination, i18n.language]
  );


  const onSearch = useCallback(
    async (params = {}) => {
      try {
        dispatch(setIsLoading(true));
        setSearch({ ...params })
        const response = await services.getUserList({
          ...params,
          requestId: generateRandomString(),
          companyId: currentCompany,
          ...pagination,
          sortBy: params.sortBy,
          page: 0,
          pageNo: 0,
        });
        dispatch(setUserList(response?.data?.content || []));
        setPagination({
          ...pagination,
          current: response?.data?.pageNo + 1,
          pageNo: response?.data?.pageNo + 1,
          total: response?.data?.totalItems,
        });
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, pagination, setPagination]
  );


  const onEdit = (record) => {
    const modifiedValues = {
      ...record,
      roles: record?.role.map((role) => role.id),
      username: record?.userRegister?.username
    }
    setUser(modifiedValues);
    setOpenEditModal(true);
  };

  const showEditModal = () => {
    setOpenEditModal(true);
  };

  const handleEditModalOk = useCallback((values) => {
    setOpenEditModal(false);
  }, []);

  const onCancel = useCallback(() => {
    setOpenEditModal(false);
    setSearch({})
  })

  const resetPassword = useCallback(
    async (record) => {
      try {
        dispatch(setIsLoading(true));
        const values = {
          email: record?.userRegister?.username,
        };
        await services.resetPassword(values);
        message.success(t("common.success"))
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, pagination, getUserList]
  );

  const onChangePassword = (record) => {
    setSelectedUser(record);
    setIsModalOpen(true);
  }

  const changePassword = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        const modifiedValues = {
          companyId: currentCompany,
          stmUserId: selectedUser?.userRegister?.userId,
          newPassword: values?.newPassword,
          newPasswordConfirm: values?.newPasswordConfirm
        };

        await services.changePassword(modifiedValues);
        message.success(t("common.success"))
        setIsModalOpen(false);
        setSelectedUser(null);

      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch, selectedUser]
  );

  const onStatus = useCallback(
    async (val, record, del) => {
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (i18n.language === "en"
          ? <span>{del ? "Are you sure confirm to delete ?" : "Are you sure confirm to change status ?"}</span>
          : <span>{del ? "คุณยืนยันการลบใช่หรือไม่ ?" : "คุณยืนยันการเปลี่ยนสถานะใช่หรือไม่ ?"}</span>
        ),
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
        onOk: async () => {
          try {
            console.log("del", del);
            dispatch(setIsLoading(true));
            await services.manageStatus({
              userId: record?.userRegister?.userId,
              requestId: generateRandomString(),
              companyId: currentCompany,
              statusFlag: del ? 'DELETE' : (val ? "ACTIVE" : "INACTIVE"),
              actionId: currentEmployee,
            });
            getUserList({ pagination: { page: 0, size: 10 } });
            message.success(t("common.success"))
          } catch (error) {
            message.error(t("common.error"))
          } finally {
            dispatch(setIsLoading(false));
          }
        }
      })
    }, [currentCompany, currentEmployee, dispatch, getUserList])

  const onSubmit = useCallback(async (values) => {
    try {
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (
          <>
            <p>
              {(values.id
                ? t("dialog.confirmation.edit_action")
                : t("dialog.confirmation.create_action")
              )}
            </p>
          </>
        ),
        onOk() {
          if (values?.userRegister?.userId) {
            updateUserManagement(values);
          }
        },
        okText: t('common.confirm'),
        cancelText: t('common.cancel'),
      });

    } catch (error) {
      message.error(t("common.error"))
    } finally {
      dispatch(setIsLoading(false))
    }
  }, [currentCompany, currentEmployee, currentUser])

  const onSubmitChangeModal = useCallback(async (values) => {
    try {
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (
          <>
            <p>
              {t("dialog.confirmation.confirm_change_password")}
            </p>
          </>
        ),
        async onOk() {
          await changePassword(values);
        },
        okText: t('common.confirm'),
        cancelText: t('common.cancel'),
      });

    } catch (error) {
      message.error(t("common.error"))
    } finally {
      dispatch(setIsLoading(false))
    }
  }, [changePassword, dispatch, t])

  const updateUserManagement = useCallback(
    async (values = {}) => {
      try {
        dispatch(setIsLoading(true))
        await services.updateUser({
          ...values,
          userId: values?.userRegister?.userId,
          username: values?.username,
          statusCode: values?.status,
          userRegisterId: values?.id,
          companyId: currentCompany,
          actionId: currentEmployee
        })
        getUserList({ pagination: { page: 0, size: 10 } });
        onCancel();
        message.success(t("common.success"))
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, onCancel, getUserList]
  );

  const handleEditModalCancel = () => {
    setOpenEditModal(false);
  };

  const onCreate = useCallback(() => {
    navigate("/employee/create");
  }, [navigate]);

  useEffect(() => {
    if (typeof currentCompany !== "object") {
      getUserList({ pagination: { page: 0, size: 10 } });
      getRoleOptions();
      // getSupervisorList();
      getSupervisorListOption();
      // getSupervisorListOption();
      // getSupervisorUserList({ pagination: pagination });
    }
  }, [currentCompany]);

  const getRoleOptions = useCallback(
    async () => {
      try {
        const response = await services.getRoleOptions({
          requestId: generateRandomString(),
          companyId: currentCompany,
        });
        dispatch(slices.setRoleOptions(response?.data))
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false))
      }
    }, [currentCompany])

  const modifiedRoleOptions = useMemo(() => {
    const modifiedOptions = roleOptions.map((opt) => ({
      value: opt?.id,
      label: i18n.language === "en" ? opt.roleNameEn : opt.roleName,
    }));

    return modifiedOptions;
  }, [i18n.language, roleOptions]);

  const getSupervisorListOption = useCallback(
    async () => {
      try {
        const response = await services.getDropDown({
          requestId: generateRandomString(),
          companyId: currentCompany,
        })
        dispatch(slices?.setSupervisorOptions(response?.data));

      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false))
      }
    }, [currentCompany]
  )


  const modifiedSupervisorOptions = useMemo(() => {
    const modifiedOptions = supervisorOptions?.map((opt) => ({
      value: opt?.id,
      label: i18n.language === "en"
        ? `${opt.nameEn} ${opt.surNameEn}`
        : `${opt.name} ${opt.surName}`,
    }));

    return modifiedOptions;
  }, [i18n.language, supervisorOptions]);

  // const getSupervisorList = useCallback(
  //   async () => {
  //     try {
  //       const response = await services.getSupervisorOption({
  //         companyId: currentCompany,
  //         employeeId: currentEmployee
  //       })
  //       console.log("response", response);
  //       dispatch(slices?.setSupervisorListOptions(response?.data?.supervisorDetailsList));
  //     } catch (error) {
  //       Alert({ type: "error", resultObject: error })
  //     } finally {
  //       dispatch(setIsLoading(false))
  //     }
  //   }, [dispatch, currentCompany]
  // );

  const getModal = useCallback(() => {
    return <UserForm
      initialValues={currentUser}
      isModalOpen={openEditModal}
      onCancel={onCancel}
      onSubmit={onSubmit}
      roleOptions={modifiedRoleOptions}
    ></UserForm>
  }, [currentUser, openEditModal, onCancel, onSubmit, modifiedRoleOptions])

  return {
    isLoading,
    userList,
    currentUser,
    openEditModal,
    roleOptions: modifiedRoleOptions,
    supervisorOptions: modifiedSupervisorOptions,
    // supervisorOption,
    showEditModal,
    handleEditModalOk,
    handleEditModalCancel,
    onCreate,
    onEdit,
    onSearch,
    getModal,
    onStatus,
    resetPassword,
    getRoleOptions,
    onChange,
    getSupervisorListOption,
    getSupervisorUserList,
    pagination,
    isModalOpen,
    setIsModalOpen,
    onSubmitChangeModal,
    onChangePassword
  };
}

export default useUserManagement;
